import type { ReactNode } from "react";
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  FormControlLabel,
  Radio,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useResizeObserverRef } from "@wojtekmaj/react-hooks";
import { useTranslation } from "react-i18next";
import type { Except, Promisable } from "type-fest";

import type { FinishedAssessmentRow } from "../AssessmentHistory/finished_assessment_row.js";
import { useFinishedAssessmentCells } from "../AssessmentHistory/useFinishedAssessmentCells.js";

/** One finished assessment the sharer may read, as Assessment history shows it. */
export type ShareableAssessment = Except<FinishedAssessmentRow, "actions">;

/**
 * The dialog's side padding, which each row of a step carries itself so a
 * list runs edge to edge and its checkboxes line up under the heading.
 */
// Tighter on a phone, where every pixel of width is the list's.
export const gutter = { xs: 2, sm: 3 } as const;

/** How a step says how many are shown and how many chosen, alike. */
export const countTextStyle = {
  fontWeight: 600,
  whiteSpace: "nowrap",
} as const;

// Not MUI's `padding="checkbox"`, whose small-table padding outranks `sx`.
export const checkboxCellStyle = {
  pl: gutter,
  pr: 1,
  py: 0,
  width: 0,
} as const;

export const chosenTint = (theme: Theme) =>
  alpha(theme.palette.primary.main, 0.12);

/**
 * Outlines the kind chips on a chosen assessment, whose tonal fill is close
 * enough to the tint to vanish into it.
 */
export const outlinedChips = (theme: Theme) => ({
  "& .MuiChip-root": {
    outline: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
  },
});

/** The columns a step shows of each assessment, as Assessment history names them. */
export const shareColumns = ["kind", "assessor", "finished"] as const;

/**
 * The narrowest a step lays its assessments out as a table: a checkbox, a
 * kind chip and a date side by side. Narrower, they stack.
 */
const shareTableMinWidth = 520;

/**
 * Whether a step stacks its assessments, judged by the step's own width, and
 * the ref that measures it. Unmeasured reads as wide, as Assessment history
 * does.
 */
export const useStackedLayout = () => {
  const [ref, entry] = useResizeObserverRef<HTMLDivElement>();
  const width = entry?.contentRect.width;

  return {
    ref,
    stacked: width !== undefined && width < shareTableMinWidth,
  };
};

const longDate = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

/** What a finished assessment is, in the dialog's words: "Definitive, approved 2 May 2026". */
export const useAssessmentWords = () => {
  const { t } = useTranslation();

  return ({ assessment, assessorName }: ShareableAssessment): string =>
    t("shareWithGuest.choose.assessment", {
      kind: assessment.definitive
        ? t("shareWithGuest.choose.definitive")
        : t("shareWithGuest.choose.nonDefinitive"),
      finished: t(
        `personPage.assessments.history.finished.${assessment.status}`,
        { date: longDate.format(assessment.finishedAt) },
      ),
      assessor: assessorName ?? t("personPage.assessments.unknownAssessor"),
    });
};

/** One assessment the sharer may tick, drawn as its row in Assessment history is. */
export const ShareableRow = ({
  offered,
  chosen,
  onToggle,
}: {
  offered: ShareableAssessment;
  chosen: boolean;
  onToggle: () => Promisable<void>;
}) => {
  const cells = useFinishedAssessmentCells(offered);
  const wordsOf = useAssessmentWords();

  return (
    <TableRow
      hover
      selected={chosen}
      onClick={() => {
        void onToggle();
      }}
      sx={{
        cursor: "pointer",
        "&.Mui-selected, &.Mui-selected:hover": { bgcolor: chosenTint },
        "&.Mui-selected": outlinedChips,
      }}
    >
      <TableCell sx={checkboxCellStyle}>
        <Checkbox
          checked={chosen}
          slotProps={{ input: { "aria-label": wordsOf(offered) } }}
          sx={{ p: 0 }}
          // The row toggles on a click too; this keeps one click one toggle.
          onClick={(event) => {
            event.stopPropagation();
          }}
          onChange={() => {
            void onToggle();
          }}
        />
      </TableCell>
      {shareColumns.map((column) => (
        <TableCell
          key={column}
          sx={column === "finished" ? { pr: gutter } : {}}
        >
          {cells[column]}
        </TableCell>
      ))}
    </TableRow>
  );
};

/** A row of the stacked layout: a checkbox and what it chooses, side by side. */
export const StackedChoice = ({
  checkbox,
  chosen = false,
  children,
}: {
  checkbox: ReactNode;
  chosen?: boolean;
  children: ReactNode;
}) => (
  <Box
    component="label"
    sx={{
      display: "flex",
      gap: 1.5,
      alignItems: "flex-start",
      px: gutter,
      py: 1.5,
      bgcolor: chosen ? chosenTint : undefined,
      ...(chosen ? { "&": outlinedChips } : {}),
      cursor: "pointer",
    }}
  >
    {checkbox}
    {children}
  </Box>
);

/** One assessment's kind, finished date and assessor, one above another. */
export const StackedCells = ({
  assessment,
}: {
  assessment: ShareableAssessment;
}) => {
  const { t } = useTranslation();
  const cells = useFinishedAssessmentCells(assessment);

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Box>{cells.kind}</Box>
      <Typography variant="subtitle2" component="div">
        {cells.finished}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t("personPage.assessments.history.column.assessor")}: {cells.assessor}
      </Typography>
    </Stack>
  );
};

/** One assessment the sharer may tick, stacked as Assessment history stacks it. */
export const ShareableStacked = ({
  offered,
  chosen,
  onToggle,
}: {
  offered: ShareableAssessment;
  chosen: boolean;
  onToggle: () => Promisable<void>;
}) => {
  const wordsOf = useAssessmentWords();

  return (
    <StackedChoice
      chosen={chosen}
      checkbox={
        <Checkbox
          checked={chosen}
          slotProps={{ input: { "aria-label": wordsOf(offered) } }}
          onChange={() => {
            void onToggle();
          }}
          sx={{ p: 0 }}
        />
      }
    >
      <StackedCells assessment={offered} />
    </StackedChoice>
  );
};

/** One chosen assessment, as a table row with nothing to tick. */
export const ChosenRow = ({
  assessment,
}: {
  assessment: ShareableAssessment;
}) => {
  const cells = useFinishedAssessmentCells(assessment);

  return (
    <TableRow>
      {shareColumns.map((column) => (
        <TableCell key={column}>{cells[column]}</TableCell>
      ))}
    </TableRow>
  );
};

/** One choice of a radio group, drawn as a tile. */
export const ChoiceTile = ({
  value,
  picked,
  disabled,
  label,
}: {
  value: string;
  picked: boolean;
  disabled: boolean;
  label: string;
}) => (
  <FormControlLabel
    value={value}
    control={<Radio size="small" />}
    disabled={disabled}
    label={label}
    sx={{
      m: 0,
      pl: 0.5,
      pr: 2,
      border: 1,
      borderRadius: 1,
      borderColor: picked ? "primary.main" : "divider",
      bgcolor: picked ? chosenTint : undefined,
    }}
  />
);

/**
 * Cancel and a step's own action: apart where the step stacks, together at
 * the right otherwise. Not the house `CancelOrProceedActions`, which offers
 * neither the steps' `gutter` nor the stacked apart-layout.
 */
export const ShareStepActions = ({
  stacked,
  sending,
  proceedLabel,
  proceedDisabled = false,
  onCancel,
  onProceed,
}: {
  stacked: boolean;
  sending: boolean;
  proceedLabel: string;
  proceedDisabled?: boolean;
  onCancel: () => Promisable<void>;
  onProceed: () => Promisable<void>;
}) => {
  const { t } = useTranslation();

  return (
    <DialogActions
      disableSpacing
      sx={{
        p: gutter,
        gap: 1,
        justifyContent: stacked ? "space-between" : "flex-end",
      }}
    >
      <Button
        variant="outlined"
        disabled={sending}
        onClick={() => {
          void onCancel();
        }}
      >
        {t("common.cancel")}
      </Button>
      <Button
        variant="contained"
        disabled={sending || proceedDisabled}
        onClick={() => {
          void onProceed();
        }}
      >
        {proceedLabel}
      </Button>
    </DialogActions>
  );
};
